import {
  TTableColumn,
  TTableRow
} from 'components_new/molecules/SegmentVirtualizedTable'

import { DatasetType } from 'types/GlobalWidget'

/**
 * A serialised source column. The preview response carries these flat, and the
 * two name fields are not interchangeable: `athena_name` is the physical name
 * the rows are keyed by, `name` is what a person reads.
 */
type TPreviewColumn = {
  athena_name: string
  id: string
  name: string
}

/** One preview row, keyed by `athena_name`. */
type TPreviewRow = {
  [athenaName: string]: unknown
}

/**
 * A table that cannot hold data until the customer has something set up on
 * their side. The API decides the severity from the row count: a warning
 * while the table is still empty, information once it is filling.
 */
export type TSourceTableNotice = {
  action?: string
  body: string
  severity: 'error' | 'info' | 'success' | 'warning'
  title: string
}

/** What `SourceTableStore.data[sourceTableId]` holds after a preview fetch. */
export type TSourceTablePreview = {
  count: number
  data: {
    attributes: {
      columns: TPreviewColumn[]
      notice?: TSourceTableNotice | null
    }
    included: {
      preview: TPreviewRow[]
    }
  }
}

/**
 * Values are shown exactly as they arrive — no locale formatting, no
 * true/false translation. Every column is therefore a plain string column;
 * typing them would make the table format numbers and dates behind our back.
 *
 * The array is not decoration: the table's own value parser ends in
 * `(value as string[]).join(',')`, so a bare scalar throws.
 */
const toCellValue = (value: unknown): string[] =>
  value === null || value === undefined ? [] : [String(value)]

const toCellLabel = (value: unknown): string =>
  value === null || value === undefined ? '' : String(value)

export const getPreviewColumns = (
  columns: TPreviewColumn[] = []
): TTableColumn[] =>
  columns.map((column) => ({
    id: column.athena_name,
    label: column.name,
    type: DatasetType.STRING
  }))

/**
 * The table treats the first column as the row's identity — it renders
 * `segment.label` there and pins it to the left — so the source table's first
 * column becomes that.
 */
export const getPreviewRows = (
  columns: TPreviewColumn[] = [],
  rows: TPreviewRow[] = []
): TTableRow[] => {
  const [segmentColumn] = columns

  if (!segmentColumn) {
    return []
  }

  return rows.map((row) => ({
    segment: { label: toCellLabel(row[segmentColumn.athena_name]) },
    values: columns.reduce(
      (acc, column) => ({
        ...acc,
        [column.athena_name]: toCellValue(row[column.athena_name])
      }),
      {}
    )
  }))
}
